import {
    AreaZoneTemplate,
    GpsTrigger,
    Zone
} from "../../../components/types";
import {Box} from "@chakra-ui/react";
import * as React from "react";
import {FC, useCallback, useContext, useEffect, useRef} from "react";
import {AppContext, AppContextI} from "../../../components/context/ContextWrapper";
import AmbientZones from "./AmbientZones";
import {ZoneContext, ZoneContextI} from "../../../components/context/ZoneContextWrapper";
import {PolygonMapShape} from "../../../components/map/MapTypes";
import {MapContext, MapContextI} from "../../../components/context/MapContextWrapper";

type Props = {
    active: boolean
}

const AreaZonesList: FC<Props> = ({active}) => {
	const appContext: AppContextI = useContext<AppContextI>(AppContext);
	const zoneContext: ZoneContextI = useContext<ZoneContextI>(ZoneContext);
	const mapContext: MapContextI = useContext<MapContextI>(MapContext);
	const setSelectedZoneRef = useRef(zoneContext.setSelectedZone);
	const updateAreaRef = useRef(appContext.updateArea);
	const updateZoneRef = useRef(appContext.updateZone);
	const addOrReplaceMapShapeRef = useRef(mapContext.addOrReplaceMapShape);
	const replaceMapShapeGroupRef = useRef(mapContext.replaceMapShapeGroup);

	setSelectedZoneRef.current = zoneContext.setSelectedZone;
	updateAreaRef.current = appContext.updateArea;
	updateZoneRef.current = appContext.updateZone;
	addOrReplaceMapShapeRef.current = mapContext.addOrReplaceMapShape;
	replaceMapShapeGroupRef.current = mapContext.replaceMapShapeGroup;

	const area = appContext.getArea();

    const ambientRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!active) {
			setSelectedZoneRef.current(undefined);
		}
	}, [active]);

    // Scroll the relevant section into view when a zone is selected from the map.
    const selectedZone = zoneContext.getSelectedZone();
    useEffect(() => {
        if (!selectedZone) return;

        switch (selectedZone) {
            case "entry": return; // no accordion panel for entry
            case "maintenance": return; // handled in separate top-level tab
            default: break; // boundaries + ambient zones are handled in the shared split panel
        }

        setTimeout(() => {
            ambientRef.current?.scrollIntoView({block: "nearest"});
        }, 100);
    }, [selectedZone]);


	const updateTrigger = useCallback((zone: Zone, trigger: GpsTrigger, coords: google.maps.LatLngLiteral[]) => {
		let newTriggers: GpsTrigger[] | undefined = zone.gpsTriggers?.filter((gpsTrigger) => gpsTrigger.id !== trigger.id);
		if (newTriggers) {
			const newTrigger: GpsTrigger = {
                ...trigger,
                polygonCoords: coords
			} as GpsTrigger;
			newTriggers = [...newTriggers, newTrigger]

			addOrReplaceMapShapeRef.current({
				id: trigger.id,
				trigger: newTrigger
			})
		}

		updateZoneRef.current(zone, {
			...zone,
			gpsTriggers: newTriggers
		} as Zone);
	}, []);


    useEffect(() => {
        if (area && area.zones) {
            const zones = area.zones;
            let needsUpdate: boolean = false;
            if (!zones.entry) {
                zones.entry = {
                    ...AreaZoneTemplate,
                    id: "entry",
                    name: "Entry",
                }
                needsUpdate = true;
            }

            if (!zones.maintenance) {
                zones.maintenance = {
                    ...AreaZoneTemplate,
                    id: "maintenance",
                    name: "Maintenance",
                    resetApps: false
                }
                needsUpdate = true;
            }
            if (!Array.isArray(zones.ambientZones)) {
                zones.ambientZones = [];
                needsUpdate = true;
            }
			if (needsUpdate) {
				updateAreaRef.current(area, {
					...area,
					zones: {
						...zones
                    }
                });
            }

            const mappedZones: Zone[] = [
                zones.boundaries,
                zones.entry,
                zones.maintenance,
                ...(zones.ambientZones || [])
            ].filter((zone): zone is Zone => !!zone);
            const mapShapes: any = mappedZones
                .map((zone: Zone) => {
                    return zone.gpsTriggers?.map((trigger: GpsTrigger) => {
                        return {
                            id: trigger.id,
                            hoverLabel: `Zone: ${zone.name || "Unnamed zone"}`,
                            trigger: trigger,
                            setCoordsCallback: (coords: google.maps.LatLngLiteral[]) => {
                                updateTrigger(zone, trigger, coords);
                            }
                        } as PolygonMapShape
                    }).flat()
                }).flat()
                .filter(zone => zone !== undefined)

			replaceMapShapeGroupRef.current(
				(shape) => !!(shape.trigger as any)?.zoneId,
				mapShapes
			);
		}
	}, [area, updateTrigger])

    return area ? (
        <Box height={"100%"} minHeight={0} display={"flex"} flexDirection={"column"}>
            <Box ref={ambientRef} flex={1} minHeight={0}>
                <AmbientZones zones={area.zones?.ambientZones}/>
            </Box>
        </Box>
    ) : <></>;
}

export default AreaZonesList;
